import { z } from 'zod';
import {
  createProjectSchema,
  type FormData,
} from '@/pages/projects/components/CreateProject/schema';
import {
  projectBriefSchema,
  type ProjectBrief,
} from '@/api/adapter/projectBrief';
import { entityIdSchema } from '@/types/current-project';

export function projectBriefFromForm(
  values: FormData,
  objectives: string[],
  constraints: string[]
): ProjectBrief {
  const projectTypes = [
    'Other',
    'SmartContract',
    'Frontend',
    'MVP',
    'Audit',
    'MobileApp',
  ];
  const budgets = [
    'Below10000',
    'From10000To50000',
    'From50000To100000',
    'Above100000',
  ];
  const preferences = [
    'WithinOneMonth',
    'OneToThreeMonths',
    'ThreeToSixMonths',
  ];
  const lines = z.array(z.string().trim().min(1).max(2000)).max(50);
  return projectBriefSchema.parse({
    summary: values.summary,
    projectType: projectTypes[values.projectType],
    link: values.url
      ? new URL(
          /^https?:\/\//i.test(values.url)
            ? values.url
            : 'https://' + values.url
        ).href
      : null,
    objectives: lines.parse(objectives.filter((value) => value.trim())),
    constraints: lines.parse(constraints.filter((value) => value.trim())),
    indicativeBudget: { currency: 'USD', range: budgets[values.budget] },
    delivery:
      values.deliveryTime === 3
        ? {
            preference: 'SpecificDate',
            date: validDeliveryDate(values.deliveryDate),
          }
        : { preference: preferences[values.deliveryTime] },
  });
}
export function validDeliveryDate(date: string) {
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
    date.startsWith('0000') ||
    !Number.isFinite(Date.parse(date)) ||
    new Date(date).toISOString().slice(0, 10) !== date
  )
    throw new Error('Choose a valid delivery date.');
  return date;
}
const draftSchema = z.object({
  values: createProjectSchema.extend({
    title: z.string(),
    description: z.string(),
    summary: z.string(),
    url: z.string(),
    projectType: z.number().int().min(-1).max(5),
  }),
  objectives: z.array(z.string()).max(50),
  constraints: z.array(z.string()).max(50),
  createdProjectId: entityIdSchema.nullable(),
});
export type SubmissionDraft = z.infer<typeof draftSchema>;
export const submissionDraftKey = (principal: string) =>
  'abako-project-submission:' + principal;
export function loadSubmissionDraft(principal: string) {
  const raw = sessionStorage.getItem(submissionDraftKey(principal));
  if (!raw) return null;
  const result = draftSchema.safeParse(JSON.parse(raw));
  if (!result.success)
    throw new Error(
      'The saved project draft cannot be read. Do not create another project until the pending request has been checked.'
    );
  return result.data;
}
export function saveSubmissionDraft(principal: string, draft: SubmissionDraft) {
  sessionStorage.setItem(submissionDraftKey(principal), JSON.stringify(draft));
}
